import { InfoCircleOutlined, SwapOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import {
  Button,
  Divider,
  Empty,
  Radio,
  Segmented,
  Skeleton,
  Tag,
  Typography,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { DATE_FORMATS } from '../../../../../../common/constants';
import { formatDate } from '../../../../../../common/utils';
import ModalComponent from '../../../../../../components/ModalComponent';
import { ROADMAP_TOY_SWAP_SUGGESTIONS_ADMIN } from '../../../../graphql/Queries';
import {
  SWAP_SUGGESTION_STATUS,
  SWAP_SUGGESTIONS_LIMIT,
} from '../../../../ultraPlanConstants';
import {
  canWeekAddBigToy,
  getBigToyLimitMessage,
  getToyTypeSlot,
  getWeekSlots,
  isBigToy,
  isSlotOrdered,
} from '../../../../ultraPlanUtils';
import ToyThumbnail from './ToyThumbnail';

const { Text, Paragraph } = Typography;

const SOURCES = {
  WEEKS: 'WEEKS',
  SUGGESTIONS: 'SUGGESTIONS',
};

const SOURCE_OPTIONS = [
  { label: 'Choose from other weeks', value: SOURCES.WEEKS },
  { label: 'Choose from user suggestions', value: SOURCES.SUGGESTIONS },
];

const SwapSlotModal = ({
  swapSource,
  userSubscriptionId,
  childProfileId,
  weeks = [],
  maxBigToys,
  loading,
  onCancel,
  onSwap,
  onApproveSuggestion,
}) => {
  const { weekNumber, slotConfig, slot } = swapSource || {};
  const [source, setSource] = useState(SOURCES.WEEKS);
  const [selectedKey, setSelectedKey] = useState(null);
  const [activeWeekNumber, setActiveWeekNumber] = useState(null);
  const isSuggestions = source === SOURCES.SUGGESTIONS;

  const [
    fetchSuggestions,
    { data: suggestionsData, loading: isSuggestionsLoading },
  ] = useLazyQuery(ROADMAP_TOY_SWAP_SUGGESTIONS_ADMIN, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  useEffect(() => {
    setSource(SOURCES.WEEKS);
    setSelectedKey(null);
    setActiveWeekNumber(null);
  }, [swapSource]);

  const sourceWeek = weeks?.find((week) => week?.weekNumber === weekNumber);

  const getBigToyReason = (incomingToy) =>
    isBigToy(incomingToy) &&
    !canWeekAddBigToy({
      week: sourceWeek,
      replacedSlotId: slot?.id,
      maxBigToys,
    })
      ? getBigToyLimitMessage(maxBigToys, weekNumber)
      : null;

  const weekCandidates = useMemo(
    () =>
      weeks
        ?.filter((week) => week?.weekNumber !== weekNumber)
        ?.flatMap((week) =>
          getWeekSlots(week)?.map(
            ({ key, slot: targetSlot, slotConfig: targetConfig }) => {
              let disabledReason = null;
              if (!targetSlot?.toy) {
                disabledReason = 'Empty slot · use Change to assign';
              } else if (isSlotOrdered(targetSlot)) {
                disabledReason = 'Already ordered';
              } else if (targetSlot?.toyId === slot?.toyId) {
                disabledReason = `Same toy as week ${weekNumber}`;
              } else if (getBigToyReason(targetSlot?.toy)) {
                disabledReason = getBigToyReason(targetSlot?.toy);
              } else if (
                isBigToy(slot?.toy) &&
                !canWeekAddBigToy({
                  week,
                  replacedSlotId: targetSlot?.id,
                  maxBigToys,
                })
              ) {
                disabledReason = getBigToyLimitMessage(
                  maxBigToys,
                  week?.weekNumber,
                );
              }
              return {
                key: `${week?.weekNumber}-${key}`,
                toy: targetSlot?.toy,
                caption: `#${targetSlot?.toy?.toyNumber || '-'}`,
                typeConfig: targetConfig,
                weekNumber: week?.weekNumber,
                targetSlot,
                disabledReason,
              };
            },
          ),
        ),
    [weeks, weekNumber, slot, sourceWeek, maxBigToys],
  );

  const suggestionCandidates = useMemo(
    () =>
      suggestionsData?.roadmapToySwapSuggestionsAdmin?.data?.map(
        (suggestion) => ({
          key: suggestion?.id,
          suggestion,
          toy: suggestion?.suggestedToy,
          caption: `#${
            suggestion?.suggestedToy?.toyNumber || '-'
          } · Suggested ${formatDate(
            suggestion?.createdAt,
            DATE_FORMATS?.REGULAR,
          )}`,
          typeConfig: getToyTypeSlot(suggestion?.suggestedToy),
          weekNumber: suggestion?.weekNumber || weekNumber,
          disabledReason:
            suggestion?.suggestedToy?.id === slot?.toyId
              ? `Already in week ${weekNumber}`
              : getBigToyReason(suggestion?.suggestedToy),
        }),
      ) || [],
    [suggestionsData, slot, weekNumber, sourceWeek, maxBigToys],
  );

  const weekGroups = useMemo(
    () =>
      weekCandidates
        ?.reduce((groups, candidate) => {
          const group = groups?.find(
            (item) => item?.weekNumber === candidate?.weekNumber,
          );
          if (group) {
            group.items.push(candidate);
            if (!candidate?.disabledReason) group.availableCount += 1;
            return groups;
          }
          return [
            ...groups,
            {
              weekNumber: candidate?.weekNumber,
              items: [candidate],
              availableCount: candidate?.disabledReason ? 0 : 1,
            },
          ];
        }, [])
        ?.filter((group) => group?.availableCount > 0),
    [weekCandidates],
  );

  const activeWeekGroup =
    weekGroups?.find((group) => group?.weekNumber === activeWeekNumber) ||
    weekGroups?.[0];

  const allCandidates = isSuggestions ? suggestionCandidates : weekCandidates;
  const candidates = isSuggestions
    ? suggestionCandidates
    : activeWeekGroup?.items || [];
  const selected = allCandidates?.find(
    (candidate) => candidate?.key === selectedKey,
  );
  const availableCount = candidates?.filter(
    (candidate) => !candidate?.disabledReason,
  )?.length;
  const slotLabel = slotConfig?.label?.toLowerCase() || 'toy';
  const isSuggestionsLoadingView = isSuggestions && isSuggestionsLoading;

  const handleSourceChange = (value) => {
    setSource(value);
    setSelectedKey(null);
    if (value === SOURCES.SUGGESTIONS && weekNumber) {
      fetchSuggestions({
        variables: {
          filter: {
            userSubscriptionId,
            childProfileId,
            weekNumber,
            status: SWAP_SUGGESTION_STATUS?.PENDING,
            skip: 0,
            limit: SWAP_SUGGESTIONS_LIMIT,
          },
        },
      });
    }
  };

  const handleConfirm = () => {
    if (isSuggestions) onApproveSuggestion?.(selected?.suggestion);
    else onSwap?.(selected);
  };

  const getConfirmLabel = () => {
    if (!selected) return isSuggestions ? 'Use Toy' : 'Swap Toys';
    return isSuggestions
      ? 'Use This Toy'
      : `Swap with Week ${selected?.weekNumber}`;
  };

  const confirmButton = (
    <Button
      type="primary"
      icon={isSuggestions ? null : <SwapOutlined />}
      disabled={!selected}
      loading={loading}
      onClick={handleConfirm}
    >
      {getConfirmLabel()}
    </Button>
  );

  const footer = (
    <div className="swap-modal-footer">
      <Text type="secondary" className="ultra-plan-caption">
        <InfoCircleOutlined />{' '}
        {isSuggestions
          ? `The suggested toy replaces this week's ${slotLabel}. Ordered slots can’t be changed.`
          : 'Ordered slots can’t be swapped. If a week is rejected, the swap is rolled back.'}
      </Text>
      <div className="swap-modal-footer-actions">
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        {confirmButton}
      </div>
    </div>
  );

  const getEmptyMessage = () => {
    if (isSuggestions) {
      return `No pending ${slotLabel} requests from the parent for this week.`;
    }
    return weekCandidates?.length
      ? 'No toys available to swap in other weeks.'
      : 'Add another week to swap toys between weeks.';
  };

  const renderEmpty = () => (
    <Empty
      className="swap-empty"
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={getEmptyMessage()}
    />
  );

  const renderCandidates = () => {
    if (isSuggestionsLoadingView) {
      return (
        <div className="swap-week-list">
          <Skeleton active avatar paragraph={{ rows: 1 }} />
          <Skeleton active avatar paragraph={{ rows: 1 }} />
        </div>
      );
    }
    if (!candidates?.length) return renderEmpty();
    return (
      <Radio.Group
        className="swap-week-list"
        value={selectedKey}
        onChange={(event) => setSelectedKey(event?.target?.value)}
        disabled={loading}
      >
        {candidates?.map((candidate) => {
          const isSelected = candidate?.key === selectedKey;
          return (
            <Radio
              key={candidate?.key}
              value={candidate?.key}
              disabled={!!candidate?.disabledReason}
              className={`swap-week-row ${isSelected ? 'is-selected' : ''}`}
            >
              <div className="swap-week-row-content">
                <ToyThumbnail toy={candidate?.toy} size={44} preview={false} />
                <div className="swap-week-row-info">
                  <Text
                    strong={!candidate?.disabledReason}
                    ellipsis={{ tooltip: candidate?.toy?.name }}
                  >
                    {candidate?.toy?.name || 'No toy assigned'}
                  </Text>
                  <div className="swap-week-row-meta">
                    {isSuggestions && candidate?.weekNumber && (
                      <Tag className="swap-week-pill">
                        {`Week ${candidate?.weekNumber}`}
                      </Tag>
                    )}
                    {candidate?.typeConfig && (
                      <Tag color={candidate?.typeConfig?.color}>
                        {candidate?.typeConfig?.label}
                      </Tag>
                    )}
                    <Text
                      type={candidate?.disabledReason ? 'warning' : 'secondary'}
                      className="ultra-plan-caption"
                      ellipsis
                    >
                      {candidate?.disabledReason || candidate?.caption}
                    </Text>
                  </div>
                </div>
              </div>
            </Radio>
          );
        })}
      </Radio.Group>
    );
  };

  return (
    <ModalComponent
      open={!!swapSource}
      width={820}
      title={
        isSuggestions
          ? `Replace ${slotLabel} with a user suggestion`
          : `Swap ${slotLabel} with another week`
      }
      footer={footer}
      onCancel={loading ? undefined : onCancel}
      closable={!loading}
      destroyOnClose
    >
      <Divider />
      <div className="swap-layout">
        <div className="swap-summary">
          <div className="swap-summary-header">
            <Tag className="swap-week-pill">{`Week ${weekNumber}`}</Tag>
            <Tag color={slotConfig?.color}>{slotConfig?.label}</Tag>
          </div>
          <div className="swap-summary-source">
            <ToyThumbnail toy={slot?.toy} size={88} />
            <Paragraph
              strong
              className="swap-summary-name"
              ellipsis={{ rows: 2, tooltip: slot?.toy?.name }}
            >
              {slot?.toy?.name || '-'}
            </Paragraph>
            <Text type="secondary" className="ultra-plan-caption">
              {`#${slot?.toy?.toyNumber || '-'}`}
            </Text>
          </div>
          <div className="swap-summary-divider">
            <span className="swap-summary-icon">
              <SwapOutlined rotate={90} />
            </span>
          </div>
          {selected ? (
            <div className="swap-summary-target">
              <ToyThumbnail toy={selected?.toy} size={44} preview={false} />
              <div className="swap-summary-target-info">
                <Text type="secondary" className="ultra-plan-caption">
                  {isSuggestions
                    ? 'User suggestion'
                    : `From week ${selected?.weekNumber}`}
                </Text>
                <Text strong ellipsis={{ tooltip: selected?.toy?.name }}>
                  {selected?.toy?.name || '-'}
                </Text>
              </div>
              {selected?.suggestion?.note && (
                <Paragraph
                  type="secondary"
                  className="swap-summary-note"
                  ellipsis={{ rows: 2, tooltip: selected?.suggestion?.note }}
                >
                  {`“${selected?.suggestion?.note}”`}
                </Paragraph>
              )}
            </div>
          ) : (
            <div className="swap-summary-target is-placeholder">
              <Text type="secondary" className="ultra-plan-caption">
                {isSuggestions
                  ? 'Choose a suggested toy'
                  : 'Choose a toy to swap with'}
              </Text>
            </div>
          )}
        </div>

        <div className="swap-picker">
          <Segmented
            block
            className="swap-source-toggle"
            options={SOURCE_OPTIONS}
            value={source}
            onChange={handleSourceChange}
            disabled={loading}
          />
          <div className="swap-picker-header">
            {isSuggestions ? (
              <Text strong>Suggested by the user</Text>
            ) : (
              <nav
                className="roadmap-week-nav swap-week-nav"
                aria-label="Weeks"
              >
                {weekGroups?.map((group) => {
                  const isActive =
                    group?.weekNumber === activeWeekGroup?.weekNumber;
                  return (
                    <button
                      key={group?.weekNumber}
                      type="button"
                      className={`roadmap-week-pill ${
                        isActive ? 'is-active' : ''
                      }`}
                      aria-current={isActive ? 'true' : undefined}
                      disabled={loading}
                      onClick={() => setActiveWeekNumber(group?.weekNumber)}
                    >
                      {`Week ${group?.weekNumber}`}
                      <span className="swap-week-nav-count">
                        {group?.availableCount}
                      </span>
                    </button>
                  );
                })}
              </nav>
            )}
            <span className="swap-picker-meta">
              {!isSuggestionsLoadingView && (
                <Text type="secondary" className="ultra-plan-caption">
                  {`${availableCount} of ${candidates?.length} available`}
                </Text>
              )}
            </span>
          </div>
          {renderCandidates()}
        </div>
      </div>
    </ModalComponent>
  );
};

export default SwapSlotModal;
